import { useEffect, useEffectEvent, useState } from 'react';

import { useKioskSession } from '@/lib/kioskSession';
import type { CandidateProfile } from '@/services/candidateProfile';
import { fetchCandidateProfile } from '@/services/candidateProfile';

import type { ProfileUpdateValues } from '../types';
import { EMPTY_PROFILE_UPDATE, mergeOnFile } from '../types';

export interface ReturningProfile {
  /** True while the matched record's read is outstanding [0464 R1; ISS-228]. */
  readonly loading: boolean;
  /**
   * The record as the screen shows it before anybody touches it — what a change is measured against
   * [0464 R3–R7]. {@link EMPTY_PROFILE_UPDATE} until the read answers, and after a read that failed.
   */
  readonly baseline: ProfileUpdateValues;
  /**
   * Whether the record already holds the consent to contact [0507]. `false` until the read answers, after a
   * read that failed, and whenever the record does not say `true` — so the modal is asked.
   */
  readonly consentOnFile: boolean;
}

interface Loaded {
  readonly for: number;
  readonly baseline: ProfileUpdateValues;
  readonly consentOnFile: boolean;
}

/**
 * Reads the matched candidate's Bullhorn record once, when the returning profile screen appears [0464 R2].
 *
 * `onLoad` receives the record the moment it lands, so the screen can lay it under what the candidate has
 * already typed — a read that lands late never overwrites a field they touched.
 *
 * ⛔ **Loading is derived from the id the last answer was FOR**, not a boolean flipped in the effect, so a
 * different id waits again. A read that fails still answers — `fetchCandidateProfile` never rejects and times
 * out — so the screen is never held for good [BR-K7]. No id, no read, nothing to wait for.
 */
export function useReturningProfile(onLoad: (onFile: CandidateProfile) => void): ReturningProfile {
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const arrived = useEffectEvent(onLoad);

  useEffect(() => {
    if (candidateId === undefined) return;
    let live = true;
    void fetchCandidateProfile(candidateId).then((onFile) => {
      // The screen can unmount mid-flight — Exit, or the idle timer — and one candidate's record must not
      // land on the next person's screen.
      if (!live) return;
      setLoaded({
        for: candidateId,
        baseline: mergeOnFile(EMPTY_PROFILE_UPDATE, onFile),
        consentOnFile: onFile.consentOnFile === true,
      });
      arrived(onFile);
    });
    return () => {
      live = false;
    };
  }, [candidateId]);

  const current = loaded !== undefined && loaded.for === candidateId ? loaded : undefined;
  return {
    loading: candidateId !== undefined && current === undefined,
    baseline: current?.baseline ?? EMPTY_PROFILE_UPDATE,
    consentOnFile: current?.consentOnFile ?? false,
  };
}
